import React, { useCallback, useRef, useState } from 'react'
import { TValidatorFn } from '@utils/customFormValidators'
import { ISelectOption } from '@typescript'
import { ParsedCountry } from 'react-international-phone'
import { convert2DigitCountryCodeTo3Digit } from '@utils/convert2DigitCountryCodeTo3Digit'
import { useFormValidation } from '@shared/hooks/useFormValidation'

export interface IInternationPhoneMeta {
  country: ParsedCountry
  inputValue: string
}

export interface IPhoneNumber {
  countryCode?: string
  phoneId?: string
  phoneNumber?: string
  phonePk?: string
}

export interface IFormField {
  autoComplete?: string
  clearsOtherFields?: string[] // IDs of other fields to clear when this field changes
  disabled?: boolean
  disableDialCodeAndPrefix?: boolean // Used exclusively for InternationalPhone type
  id: string
  inputMode?: 'decimal' | 'email' | 'none' | 'numeric' | 'search' | 'tel' | 'text' | 'url'
  label: string
  max?: number
  min?: number
  maxLength?: number
  minLength?: number
  name: string
  options?: ISelectOption[]
  placeholder?: string
  required?: boolean
  sanitizeInput?: (value: any) => string | null | undefined
  showDisabledDialCodeAndPrefix?: boolean // Used exclusively for InternationalPhone type
  type: 'text' | 'email' | 'password' | 'number' | 'checkbox' | 'radio' | 'select' | 'tel' | 'textarea'
  value?: string | number | boolean | null | undefined | IPhoneNumber
  validators?: TValidatorFn[]

  [key: string]: any
}

export type TFormModel = {
  fields: Record<string, IFormField>
  initialState: Record<string, any>
}

function isInternationalPhoneMeta(value: unknown): value is IInternationPhoneMeta {
  return (
    typeof value === 'object' &&
    value !== null &&
    'inputValue' in value &&
    'country' in value &&
    !!(value as IInternationPhoneMeta).country
  )
}

export function useForm(formModel: TFormModel) {
  const { fields, initialState } = formModel
  const formRef = useRef<HTMLFormElement>(null)
  const [values, setValues] = useState<Record<string, any>>(initialState)

  const { errors, setErrors, validateField, validateAll, focusFirstInvalidField } = useFormValidation(fields, values, formRef)

  /** Handle value change */
  const handleChange = useCallback(
    (fieldId: string, rawValue: boolean | number | string | IInternationPhoneMeta) => {
      let newValue = rawValue
      // Special handling for phone number fields, due to IPhoneNumber model
      if (isInternationalPhoneMeta(newValue)) {
        newValue = {
          ...values?.[fieldId],
          phoneNumber: newValue.inputValue ?? '',
          countryCode: convert2DigitCountryCodeTo3Digit(newValue.country.iso2),
        }
      }

      const newValues = {
        ...values,
        [fieldId]: newValue,
      }

      // Clear dependent fields, if specified (clearsOtherFields)
      const field = fields[fieldId]
      if (field && Array.isArray(field.clearsOtherFields) && field.clearsOtherFields.length > 0) {
        field.clearsOtherFields.forEach((clearId) => {
          if (newValues[clearId] !== undefined) {
            newValues[clearId] = ''
          }
        })
      }

      // Update values and reset this field’s error
      setValues(newValues)
      setErrors((prev) => ({
        ...prev,
        [fieldId]: null,
      }))
    },
    [values, fields],
  )

  const setValue = useCallback((fieldId: string, value: any) => handleChange(fieldId, value), [handleChange])

  /** Submit handler with async validation */
  const handleSubmit = useCallback(
    (onSubmit: (values: Record<string, any>) => void) => async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      const isAllValid = await validateAll()
      if (!isAllValid) {
        focusFirstInvalidField()
      } else {
        onSubmit(values)
      }
    },
    [validateAll, values, focusFirstInvalidField],
  )

  return {
    formRef,
    values,
    errors,
    handleChange,
    validateField,
    validateAll,
    handleSubmit,
    setValue,
  }
}
